// Blog Editor - Create/Edit Blog Posts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImageExtension from 'tiptap-extension-resize-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import '../../App.css';
import './BlogEditor.css';
import Footer from '../Footer';

export default function BlogEditor() {
  const { instance, accounts } = useMsal();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFromUrl = queryParams.get('category') || 'azure';
  
  // Extract blog ID from URL path (e.g., /blog/edit/blog-id)
  const pathParts = location.pathname.split('/');
  const isEditMode = pathParts.includes('edit');
  const blogId = isEditMode ? pathParts[pathParts.length - 1] : null;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(categoryFromUrl);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [thumbnailPosition, setThumbnailPosition] = useState(50);
  const thumbDragRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getAuthToken = async () => {
    if (!accounts[0]) return null;
    const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return result.idToken;
  };

  // Handle image upload
  const handleImageUpload = useCallback(async (file) => {
    try {
      setImageUploading(true);
      console.log('Uploading file:', { name: file.name, type: file.type, size: file.size });
      
      const formData = new FormData();
      formData.append('image', file);

      const token = await getAuthToken();
      const response = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Upload successful:', data);
        return data.url;
      } else {
        const errorText = await response.text();
        console.error('Upload failed with status:', response.status, errorText);
        throw new Error(`Upload failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Failed to upload image: ${error.message}`);
      return null;
    } finally {
      setImageUploading(false);
    }
  }, []);

  // TipTap editor setup
  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImageExtension,
      Link.configure({
        openOnClick: false,
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
      // Clean up Word/Office HTML on paste: strip mso-* styles, o:p tags, XML
      // namespaced elements and conditional comments while keeping semantic tags.
      transformPastedHTML(html) {
        // Remove Word conditional comments, XML namespace declarations
        let clean = html
          .replace(/<!--\[if[\s\S]*?\[endif\]-->/gi, '')
          .replace(/<o:[^>]*>.*?<\/o:[^>]*>/gis, '')
          .replace(/<o:[^>]*\/>/gi, '')
          .replace(/<w:[^>]*>.*?<\/w:[^>]*>/gis, '')
          .replace(/<m:[^>]*>.*?<\/m:[^>]*>/gis, '');

        // Strip mso-* and font-family: inline styles that bloat the output
        clean = clean.replace(
          / style="[^"]*"/gi,
          (match) => {
            // Keep only useful CSS properties, drop mso- and font garbage
            const useful = match
              .replace(/mso-[^;"]+;?/gi, '')
              .replace(/font-family:[^;"]+;?/gi, '')
              .replace(/font-size:[^;"]+;?/gi, '')
              .replace(/color:\s*(?:black|windowtext);?/gi, '')
              .replace(/ style="\s*"/gi, '');
            return useful.trim() === 'style=""' ? '' : useful;
          }
        );

        return clean;
      },
    },
  });

  // Load existing blog data if in edit mode
  useEffect(() => {
    const fetchBlogData = async () => {
      if (!isEditMode || !blogId) return;
      
      try {
        setLoading(true);
        const response = await fetch(`/api/blogs/${blogId}`);
        if (response.ok) {
          const data = await response.json();
          setTitle(data.title);
          setCategory(data.category);
          setTags(data.tags || []);
          setExcerpt(data.excerpt || '');
          setThumbnail(data.thumbnail || '');
          setThumbnailPosition(data.thumbnailPosition ?? 50);
          // Set editor content
          if (editor && data.content) {
            editor.commands.setContent(data.content);
          }
        } else {
          alert('Failed to load blog post');
          history.goBack();
        }
      } catch (error) {
        console.error('Error loading blog:', error);
        alert('Failed to load blog post');
        history.goBack();
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, [isEditMode, blogId, editor, history]);

  const handleThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    thumbDragRef.current = { startY: e.clientY, startPos: thumbnailPosition };

    const handleMouseMove = (ev) => {
      if (!thumbDragRef.current) return;
      const { startY, startPos } = thumbDragRef.current;
      const deltaY = ev.clientY - startY;
      const newPos = Math.max(0, Math.min(100, startPos + deltaY / 2));
      setThumbnailPosition(newPos);
    };

    const handleMouseUp = () => {
      thumbDragRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [thumbnailPosition]);

  // Add image to editor
  const addImage = useCallback(async (file) => {
    const url = await handleImageUpload(file);
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor, handleImageUpload]);

  // Handle paste events for image uploads.
  // IMPORTANT: Word (and other apps) put both HTML/text AND a PNG render in the clipboard.
  // Only intercept if it's a pure image paste (no text content alongside it), otherwise
  // we'd block Word pastes entirely.
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      if (!items.length) return;

      const hasText = items.some(
        (item) => item.type === 'text/html' || item.type === 'text/plain'
      );
      const imageItem = items.find((item) => item.type.startsWith('image/'));

      // Only upload the image if there's no text — i.e. it's a screenshot paste
      if (imageItem && !hasText) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          await addImage(file);
        }
      }
      // Otherwise let TipTap handle it (Word, browser copy, etc.)
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [addImage]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim().toLowerCase())) {
      setTags([...tags, tagInput.trim().toLowerCase()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setSuccessMessage('Please enter a title');
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2500);
      return;
    }

    const content = editor?.getHTML() || '';
    
    if (!content.trim() || content === '<p></p>') {
      setSuccessMessage('Please enter some content');
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2500);
      return;
    }

    try {
      setSaving(true);
      // Prefer profile name from Profile Account page
      let authorName = accounts[0]?.name || 'Admin';
      try {
        const profileRes = await fetch('/api/profile-data?userId=' + accounts[0]?.localAccountId);
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          if (profileData?.firstName || profileData?.secondName) {
            authorName = `${profileData.firstName || ''} ${profileData.secondName || ''}`.trim();
          }
        }
      } catch (e) { /* use MSAL name */ }

      const blogData = {
        title,
        content,
        excerpt: excerpt || content.substring(0, 150) + '...',
        thumbnail,
        thumbnailPosition,
        category,
        tags,
        author: authorName,
        authorId: accounts[0]?.localAccountId
      };

      // Add createdAt only for new posts
      if (!isEditMode) {
        blogData.createdAt = new Date().toISOString();
      }

      // Use PUT for editing, POST for creating
      const url = isEditMode ? `/api/blogs/${blogId}` : '/api/blogs';
      const method = isEditMode ? 'PUT' : 'POST';

      const token = await getAuthToken();
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(blogData)
      });

      if (response.ok) {
        setSuccessMessage(`Blog post ${isEditMode ? 'updated' : 'published'} successfully!`);
        setShowSuccessModal(true);
        // Redirect after 2 seconds
        setTimeout(() => {
          history.push(`/content/${category}`);
        }, 2000);
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      setSuccessMessage('Failed to save blog post. Please try again.');
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = await getAuthToken();
      const response = await fetch(`/api/blogs/${blogId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        setShowDeleteConfirm(false);
        setSuccessMessage('Blog post deleted successfully!');
        setShowSuccessModal(true);
        setTimeout(() => {
          history.push(`/content/${category}`);
        }, 1500);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      setShowDeleteConfirm(false);
      setSuccessMessage('Failed to delete blog post. Please try again.');
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-editor-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading blog post...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="success-modal-overlay">
          <div className="success-modal delete-confirm-modal">
            <div className="success-icon delete-icon">
              <i className="fas fa-trash-alt"></i>
            </div>
            <h2>Delete Post?</h2>
            <p>This will permanently delete <strong>"{title}"</strong>. This cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-delete-confirm"
              >
                {deleting ? <><i className="fas fa-spinner fa-spin"></i> Deleting...</> : <><i className="fas fa-trash-alt"></i> Yes, Delete</>}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-cancel"
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">
              {successMessage.includes('successfully') ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-exclamation-circle"></i>
              )}
            </div>
            <h2>{successMessage.includes('successfully') ? '🎉 Success!' : 'Oops!'}</h2>
            <p>{successMessage}</p>
            {successMessage.includes('successfully') && (
              <div className="success-animation">
                <div className="confetti"></div>
                <div className="confetti"></div>
                <div className="confetti"></div>
                <div className="confetti"></div>
                <div className="confetti"></div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="blog-editor-container">
        <div className="blog-editor-header">
          <h1>{isEditMode ? 'Edit Blog Post' : 'Create Blog Post'}</h1>
          <button onClick={() => history.goBack()} className="btn-back">
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>

        <div className="blog-editor-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              placeholder="Enter blog post title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="blog-input"
            />
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="blog-select"
            >
              <option value="azure">Azure</option>
              <option value="ai">AI</option>
              <option value="m365">M365</option>
              <option value="general">General</option>
            </select>
          </div>

          <div className="form-group">
            <label>Thumbnail Image</label>
            <div className="thumbnail-upload-area">
              {thumbnail ? (
                <div
                  className="thumbnail-preview"
                  onMouseDown={handleThumbMouseDown}
                  title="Drag up/down to reposition"
                >
                  <img
                    src={thumbnail}
                    alt="Thumbnail preview"
                    style={{ objectPosition: `center ${thumbnailPosition}%` }}
                    draggable={false}
                  />
                  <div className="thumbnail-drag-hint">
                    <i className="fas fa-arrows-alt-v"></i> Drag to reposition
                  </div>
                  <button
                    type="button"
                    className="thumbnail-remove-btn"
                    onClick={(e) => { e.stopPropagation(); setThumbnail(''); }}
                  >
                    <i className="fas fa-times"></i> Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="thumbnail-upload-btn"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const url = await handleImageUpload(file);
                        if (url) setThumbnail(url);
                      }
                    };
                    input.click();
                  }}
                >
                  <i className="fas fa-image"></i> Upload Thumbnail
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Excerpt (Optional)</label>
            <textarea
              placeholder="Brief description (auto-generated if left empty)..."
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="blog-textarea"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Tags</label>
            <div className="tags-input-container">
              <input
                type="text"
                placeholder="Add a tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="tag-input"
              />
              <button onClick={handleAddTag} className="btn-add-tag">
                <i className="fas fa-plus"></i> Add
              </button>
            </div>
            <div className="tags-display">
              {tags.map(tag => (
                <span key={tag} className="tag-item">
                  #{tag}
                  <button onClick={() => handleRemoveTag(tag)} className="btn-remove-tag">
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Content *</label>
            <div className="editor-info">
              <p>
                <i className="fas fa-info-circle"></i> Write and format your content directly in the editor below.
                Use the toolbar for formatting or keyboard shortcuts.
              </p>
              <p>
                <i className="fas fa-image"></i> <strong>Images:</strong> Copy a screenshot (Cmd+C/Ctrl+C) and paste it directly into the editor!
                Click & drag image corners to resize.
              </p>
              {imageUploading && (
                <p className="uploading-indicator">
                  <i className="fas fa-spinner fa-spin"></i> Uploading image...
                </p>
              )}
            </div>
            <div className="tiptap-wrapper">
              {editor && (
                <div className="tiptap-toolbar">
                  <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                  >
                    H1
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                  >
                    H2
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                  >
                    H3
                  </button>
                  <span className="separator"></span>
                  <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'is-active' : ''}
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'is-active' : ''}
                  >
                    <em>I</em>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={editor.isActive('underline') ? 'is-active' : ''}
                  >
                    <u>U</u>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={editor.isActive('strike') ? 'is-active' : ''}
                  >
                    <s>S</s>
                  </button>
                  <span className="separator"></span>
                  <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editor.isActive('bulletList') ? 'is-active' : ''}
                    title="Bullet List"
                  >
                    • List
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={editor.isActive('orderedList') ? 'is-active' : ''}
                    title="Numbered List"
                  >
                    1. List
                  </button>
                  <span className="separator"></span>
                  <button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                    title="Align Left"
                  >
                    <i className="fas fa-align-left"></i>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                    title="Align Center"
                  >
                    <i className="fas fa-align-center"></i>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                    title="Align Right"
                  >
                    <i className="fas fa-align-right"></i>
                  </button>
                  <button
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
                    title="Justify"
                  >
                    <i className="fas fa-align-justify"></i>
                  </button>
                  <span className="separator"></span>
                  <button
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={editor.isActive('blockquote') ? 'is-active' : ''}
                  >
                    " Quote
                  </button>
                  <button
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    className={editor.isActive('codeBlock') ? 'is-active' : ''}
                  >
                    {'</>'}
                  </button>
                  <span className="separator"></span>
                  <button
                    title="Insert Note callout"
                    onClick={() => editor.chain().focus().setCodeBlock({ language: 'note' }).run()}
                    className={editor.isActive('codeBlock', { language: 'note' }) ? 'is-active' : ''}
                  >
                    📝 Note
                  </button>
                  <button
                    title="Insert Warning callout"
                    onClick={() => editor.chain().focus().setCodeBlock({ language: 'warning' }).run()}
                    className={editor.isActive('codeBlock', { language: 'warning' }) ? 'is-active' : ''}
                  >
                    ⚠️ Warning
                  </button>
                  <span className="separator"></span>
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          await addImage(file);
                        }
                      };
                      input.click();
                    }}
                  >
                    🖼️ Image
                  </button>
                </div>
              )}
              <EditorContent editor={editor} className="tiptap-editor" />
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={handleSave}
              disabled={saving || imageUploading}
              className="btn-save"
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Publish Post
                </>
              )}
            </button>
            <button onClick={() => history.goBack()} className="btn-cancel">
              Cancel
            </button>
            {isEditMode && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving || deleting}
                className="btn-delete"
              >
                <i className="fas fa-trash-alt"></i> Delete Post
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
